import { useMemo, useState } from 'react';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { LineChart } from '@mui/x-charts/LineChart';
import { usePreisToolPriceHistory } from '@/api/preis-tool/preis-tool.hooks';
import type { PreisToolPriceHistorySeriesKey } from '@/api/preis-tool/preis-tool.types';
import { DsSegmented } from '@/modules/internal-v2/ds';
import { fmtEUR } from '../helpers';
import { PriceChangeLog } from './price-change-log.component';

interface PriceHistoryProps {
  itemId: string;
}

const PERIODS = [14, 30, 90, 180, 365, 730] as const;
type Period = (typeof PERIODS)[number];

const SERIES: { key: PreisToolPriceHistorySeriesKey; label: string; color: string }[] = [
  { key: 'mek', label: 'MEK', color: 'var(--ink-2)' },
  { key: 'standardPrice', label: 'Standard', color: 'var(--accent)' },
  { key: 'itscopePrice', label: 'ITscope', color: 'var(--pos)' },
  { key: 'onlinerPrice', label: 'Onliner', color: 'var(--warn)' },
];

const fmtDay = (d: Date) => d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
const fmtDayWithYear = (d: Date) =>
  d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

/** At most this many date labels under the chart. */
const MAX_TICKS = 10;

/** The start of every day from `from` to `to` (local time). */
function dayStarts(from: Date, to: Date): Date[] {
  const day = new Date(from);
  day.setHours(0, 0, 0, 0);
  if (day < from) day.setDate(day.getDate() + 1);
  const days: Date[] = [];
  for (; day <= to; day.setDate(day.getDate() + 1)) days.push(new Date(day));
  return days;
}
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * MEK and the three sale prices of one item over a selectable period, with
 * the changes of that period underneath (EOS-54).
 */
export function PriceHistory({ itemId }: PriceHistoryProps) {
  const [days, setDays] = useState<Period>(30);
  const [hidden, setHidden] = useState<Set<PreisToolPriceHistorySeriesKey>>(new Set());
  const { data, isPending, isError } = usePreisToolPriceHistory(itemId, days);

  const toggle = (key: PreisToolPriceHistorySeriesKey) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const hasAnyData = SERIES.some((s) => (data?.series[s.key].length ?? 0) > 0);

  // MUI X needs one shared x axis. The backend sends one value per day (the
  // last price of that day), at the start of the day. The axis gets every day
  // of the period plus every point of the series, so hovering shows the
  // values of any day. A step series keeps its last value until its next
  // point; before its first point it has none.
  const chart = useMemo(() => {
    if (!data) return null;
    const visible = SERIES.filter((s) => !hidden.has(s.key) && data.series[s.key].length > 0);
    const days = dayStarts(new Date(data.from), new Date(data.to));
    const times = [
      ...new Set([
        ...days.map((d) => d.getTime()),
        ...visible.flatMap((s) => data.series[s.key].map((p) => new Date(p.at).getTime())),
      ]),
    ].sort((a, b) => a - b);
    // Date labels only at whole days, evenly thinned out.
    const step = Math.max(1, Math.ceil(days.length / MAX_TICKS));
    return {
      ticks: days.filter((_, i) => i % step === 0),
      xs: times.map((t) => new Date(t)),
      series: visible.map((s) => {
        const points = data.series[s.key].map((p) => ({ t: new Date(p.at).getTime(), value: p.value }));
        let next = 0;
        let current: number | null = null;
        const values = times.map((t) => {
          while (next < points.length && points[next].t <= t) current = points[next++].value;
          return current;
        });
        return {
          id: s.key,
          label: s.label,
          color: s.color,
          curve: 'stepAfter' as const,
          connectNulls: false,
          showMark: false,
          data: values,
          valueFormatter: (v: number | null) => (v == null ? '' : `€ ${fmtEUR(v)}`),
        };
      }),
    };
  }, [data, hidden]);

  const recordingStart = data?.recordingStartedAt ? new Date(data.recordingStartedAt) : null;
  const recordingStartsInRange = recordingStart != null && data != null && recordingStart > new Date(data.from);
  // The label sits right of the line; near the right edge it would be cut off, so it moves to the left.
  const recordingStartIsLate =
    recordingStart != null &&
    data != null &&
    recordingStart.getTime() > (new Date(data.from).getTime() + new Date(data.to).getTime()) / 2;

  return (
    <div className="pt-history">
      <div className="pt-supplier-offers-header" style={{ alignItems: 'center' }}>
        <DsSegmented
          ariaLabel="Zeitraum"
          size="small"
          options={PERIODS.map((p) => ({ value: p, label: `${p} Tage` }))}
          value={days}
          onChange={setDays}
        />
        {SERIES.map((s) => {
          const empty = (data?.series[s.key].length ?? 0) === 0;
          const on = !hidden.has(s.key) && !empty;
          return (
            <button
              key={s.key}
              className={`ds-toggle-chip${on ? ' on' : ''}`}
              onClick={() => toggle(s.key)}
              disabled={empty}
              title={empty ? 'Für diese Reihe ist noch nichts aufgezeichnet.' : undefined}
              style={empty ? { opacity: 0.5, cursor: 'default' } : undefined}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 1,
                  background: s.color,
                  opacity: on ? 1 : 0.3,
                  display: 'inline-block',
                }}
              />
              {s.label}
            </button>
          );
        })}
        <div className="hint" style={{ marginLeft: 'auto' }}>
          {data?.recordingStartedAt
            ? `Aufzeichnung seit ${fmtDate(data.recordingStartedAt)} · täglicher Stand aus SAP und jede übertragene Änderung`
            : 'Noch keine Aufzeichnung'}
        </div>
      </div>
      {isPending ? (
        <div className="ds-empty" style={{ flex: 1 }}>Lade…</div>
      ) : isError ? (
        <div className="ds-empty" style={{ flex: 1 }}>Fehler beim Laden des Preisverlaufs.</div>
      ) : !hasAnyData ? (
        <div className="ds-empty" style={{ flex: 1 }}>
          <div>
            <div className="big">Noch kein Verlauf für diesen Artikel</div>
            <div>
              Der Verlauf entsteht aus dem täglichen Stand von MEK und Verkaufspreisen aus dem Artikel-Abgleich und aus jeder Preisänderung, die SAP übernommen hat.
              {data?.recordingStartedAt
                ? ` Aufgezeichnet wird seit ${fmtDate(data.recordingStartedAt)}, ältere Werte gibt es nicht.`
                : ' Bisher wurde noch nichts aufgezeichnet.'}
            </div>
          </div>
        </div>
      ) : chart && chart.series.length === 0 ? (
        <div className="ds-empty" style={{ flex: 1 }}>Alle Reihen sind ausgeblendet.</div>
      ) : (
        chart && (
          <div className="pt-history-chart">
            <LineChart
              xAxis={[
                {
                  data: chart.xs,
                  scaleType: 'time',
                  min: new Date(data!.from),
                  max: new Date(data!.to),
                  tickInterval: chart.ticks,
                  valueFormatter: days > 365 ? fmtDayWithYear : fmtDay,
                  tickLabelStyle: { fontSize: 10 },
                },
              ]}
              yAxis={[{ valueFormatter: (v: number | null) => (v == null ? '' : fmtEUR(v)), width: 64 }]}
              series={chart.series}
              grid={{ horizontal: true }}
              margin={{ top: 12, right: 24, bottom: 8, left: 8 }}
              hideLegend
              skipAnimation
            >
              {recordingStartsInRange && (
                <ChartsReferenceLine
                  x={recordingStart}
                  label="Beginn der Aufzeichnung"
                  labelAlign="start"
                  spacing={recordingStartIsLate ? { x: -5 } : undefined}
                  lineStyle={{ strokeDasharray: '4 3' }}
                  labelStyle={{ fontSize: 10, textAnchor: recordingStartIsLate ? 'end' : 'start' }}
                />
              )}
            </LineChart>
          </div>
        )
      )}
      <div className="pt-history-changes-title">
        Preisänderungen im Zeitraum{data ? ` · ${data.changes.length}` : ''}
      </div>
      <PriceChangeLog
        changes={data?.changes ?? []}
        isPending={isPending}
        isError={isError}
        emptyMessage={`In den letzten ${days} Tagen wurde im Preis-Tool kein Preis geändert.`}
      />
    </div>
  );
}
